import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { useUXPContext } from "@core/context/useUXPContext";
import SearchBox from "@widget/forms/inputs/SearchBox";
import useDebounce from "@hooks/useDebounce";
import { SkeletonLoader } from "@widget/loaders";
import MediaTile from "./MediaTile";
import MediaPreviewPanel from "./MediaPreviewPanel";
import useInfiniteScroll from "@hooks/useInfiniteScroll";
const ICONS_PER_BATCH = 100;
// A batch is far more cards than a viewport holds — the placeholder grid only
// has to fill the visible rows.
const LOADING_SKELETONS = Math.min(ICONS_PER_BATCH, 24);
/**
 * @export
 * The font-icon browser: a searchable grid of icons beside the shared preview
 * column, which carries the grid of every variant the previewed icon is
 * published in. Mounted by MediaPickerModal for its Icons source.
 */
const IconsView = (props) => {
    const { show, onPick, isSelected, onPreview, onPendingChange, previewSelected, onPreviewToggle, currentValue } = props;
    const context = useUXPContext();
    const [searchQuery, setSearchQuery] = React.useState("");
    const [allFilteredIcons, setAllFilteredIcons] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(false);
    // Infinite scroll state — searchIcons is in-memory, so paging is a slice
    const [visibleCount, setVisibleCount] = React.useState(ICONS_PER_BATCH);
    // Preview column state
    const [selectedIcon, setSelectedIcon] = React.useState(null);
    const [selectedPackage, setSelectedPackage] = React.useState(null);
    const [selectedWeight, setSelectedWeight] = React.useState(null);
    const debouncedQuery = useDebounce(searchQuery, 300);
    const IconMetadata = React.useMemo(() => {
        return (typeof window !== 'undefined' && window.IconMetadata);
    }, []);
    // Search icons when the query changes - sort alphabetically
    React.useEffect(() => {
        if (!IconMetadata || !show)
            return;
        setIsLoading(true);
        try {
            const results = IconMetadata.searchIcons(debouncedQuery);
            // Sort alphabetically by displayName
            const sortedResults = results.sort((a, b) => a.displayName.localeCompare(b.displayName));
            setAllFilteredIcons(sortedResults);
            setVisibleCount(ICONS_PER_BATCH); // Reset to the first batch on new search
        }
        catch (error) {
            console.error('Error searching icons:', error);
            setAllFilteredIcons([]);
        }
        finally {
            setIsLoading(false);
        }
    }, [debouncedQuery, IconMetadata, show]);
    const visibleIcons = React.useMemo(() => {
        return allFilteredIcons.slice(0, visibleCount);
    }, [allFilteredIcons, visibleCount]);
    const { ref: resultsRef, onScroll: handleResultsScroll } = useInfiniteScroll({
        loading: isLoading,
        hasMore: visibleCount < allFilteredIcons.length,
        count: visibleIcons.length,
        resetKey: debouncedQuery,
        onLoadMore: () => setVisibleCount(prev => prev + ICONS_PER_BATCH),
    });
    // Reset when closing
    React.useEffect(() => {
        if (!show) {
            clearPreview();
            setSearchQuery("");
            setVisibleCount(ICONS_PER_BATCH);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);
    // The host lays out around this column (and commits from its header in
    // single mode), so it needs the string the preview currently resolves to.
    React.useEffect(() => {
        onPendingChange === null || onPendingChange === void 0 ? void 0 : onPendingChange(buildPreviewIcon() || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIcon, selectedPackage, selectedWeight]);
    // Unmounting must not leave a stale preview value with the host.
    React.useEffect(() => () => onPendingChange === null || onPendingChange === void 0 ? void 0 : onPendingChange(''), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    // Get first available variant for an icon
    const getFirstAvailableVariant = (icon) => {
        if (!IconMetadata)
            return null;
        // Prefer FA solid, fallback to first available
        if (icon.packages.fa) {
            const weight = icon.packages.fa.includes('solid') ? 'solid' : icon.packages.fa[0];
            return IconMetadata.buildIconString('fa', weight, icon.name);
        }
        if (icon.packages.ph) {
            const weight = icon.packages.ph.includes('regular') ? 'regular' : icon.packages.ph[0];
            return IconMetadata.buildIconString('ph', weight, icon.name);
        }
        return null;
    };
    // Every package/weight combination an icon is published in
    const getVariants = (icon) => {
        if (!icon || !IconMetadata)
            return [];
        const variants = [];
        if (icon.packages.fa) {
            icon.packages.fa.forEach(weight => {
                const iconString = IconMetadata.buildIconString('fa', weight, icon.name);
                if (iconString)
                    variants.push({ iconString, label: `FA ${weight}`, package: 'fa', weight });
            });
        }
        if (icon.packages.ph) {
            icon.packages.ph.forEach(weight => {
                const iconString = IconMetadata.buildIconString('ph', weight, icon.name);
                if (iconString)
                    variants.push({ iconString, label: `PH ${weight}`, package: 'ph', weight });
            });
        }
        return variants;
    };
    // Open an icon in the preview column, on its most likely variant
    const openPreview = (icon) => {
        setSelectedIcon(icon);
        if (icon.packages.fa) {
            setSelectedPackage('fa');
            setSelectedWeight(icon.packages.fa.includes('solid') ? 'solid' : icon.packages.fa[0]);
        }
        else if (icon.packages.ph) {
            setSelectedPackage('ph');
            setSelectedWeight(icon.packages.ph.includes('regular') ? 'regular' : icon.packages.ph[0]);
        }
    };
    const clearPreview = () => {
        setSelectedIcon(null);
        setSelectedPackage(null);
        setSelectedWeight(null);
    };
    // Always previews, in both modes — unlike the library and gallery, whose
    // tiles toggle straight into the selection. A library item *is* its value;
    // an icon tile only stands in for a family of them, so selecting from the
    // grid would commit whichever variant the tile happened to draw.
    const handleIconClick = (icon) => {
        openPreview(icon);
    };
    // Build preview icon string
    const buildPreviewIcon = () => {
        if (!selectedIcon || !selectedPackage || !selectedWeight || !IconMetadata)
            return null;
        return IconMetadata.buildIconString(selectedPackage, selectedWeight, selectedIcon.name);
    };
    // The field's own value counts as selected too, so reopening the picker
    // badges whatever is already chosen — in single mode that is the only mark
    // there is, since there is no selection to be in.
    const isIconSelected = (icon) => {
        const variants = getVariants(icon);
        if (currentValue && variants.some(v => v.iconString === currentValue))
            return true;
        if (!isSelected)
            return false;
        return variants.some(v => isSelected(v.iconString));
    };
    // Draw a variant in the large preview, without touching the selection.
    const showVariant = (variant) => {
        setSelectedPackage(variant.package);
        setSelectedWeight(variant.weight);
    };
    // The preview column's source-specific controls: every package / weight the
    // previewed icon is published in, as the same cards the grid uses — and
    // wired the same way, since a variant is a value like any other.
    const renderPreviewControls = () => {
        if (!selectedIcon)
            return null;
        return _jsx("div", { className: "uxpcore_mediapicker__grid", children: getVariants(selectedIcon).map((variant, idx) => _jsx(MediaTile, { value: variant.iconString, name: variant.label, type: "Icon", showName: true, selected: !!(isSelected === null || isSelected === void 0 ? void 0 : isSelected(variant.iconString)) || variant.iconString === currentValue, active: selectedPackage === variant.package && selectedWeight === variant.weight, onClick: () => { showVariant(variant); onPick === null || onPick === void 0 ? void 0 : onPick(variant.iconString); }, onPreview: onPreview ? () => { showVariant(variant); onPreview(variant.iconString); } : undefined, previewTitle: context.$L('uxp-core.media-picker.preview-item') }, idx)) });
    };
    return _jsxs(_Fragment, { children: [_jsxs("div", { className: "uxpcore_mediapicker__list", children: [_jsx("div", { className: "uxpcore_mediapicker__search", children: _jsx(SearchBox, { value: searchQuery, onChange: setSearchQuery, placeholder: context.$L("uxp-core.icon-picker.search-icons"), fullWidth: true }) }), _jsx("div", { className: "uxpcore_mediapicker__results", ref: resultsRef, onScroll: handleResultsScroll, children: isLoading
                            ? _jsx("div", { className: "uxpcore_mediapicker__grid", children: Array.from({ length: LOADING_SKELETONS }, (_, i) => (_jsx("div", { className: "uxpcore_mediapicker__item uxpcore_mediapicker__item--skeleton", children: _jsx(SkeletonLoader, { width: "100%", height: "100%" }) }, `skeleton-${i}`))) })
                            : allFilteredIcons.length === 0
                                ? _jsx("div", { className: "uxpcore_mediapicker__message", children: context.$L("uxp-core.icon-picker.no-results") })
                                : _jsx("div", { className: "uxpcore_mediapicker__grid", children: visibleIcons.map((icon, idx) => {
                                        const firstVariant = getFirstAvailableVariant(icon);
                                        if (!firstVariant)
                                            return null;
                                        // No expand button — the card click already previews.
                                        return _jsx(MediaTile, { value: firstVariant, name: icon.displayName, type: "Icon", showName: true, selected: isIconSelected(icon), active: (selectedIcon === null || selectedIcon === void 0 ? void 0 : selectedIcon.name) === icon.name, onClick: () => handleIconClick(icon) }, icon.name || idx);
                                    }) }) })] }), _jsx(MediaPreviewPanel, { value: buildPreviewIcon() || '', name: selectedIcon === null || selectedIcon === void 0 ? void 0 : selectedIcon.displayName, selected: previewSelected, onToggle: onPreviewToggle, onClose: clearPreview, closeTitle: context.$L('uxp-core.text.back'), children: renderPreviewControls() })] });
};
export default IconsView;
